<template>
  <SidebarCaption v-model="isOpened" caption="Configuration name" />
  <div class="ui-pivot-table__configs" :class="mainClasses">
    <UIInputDynamicList
      v-if="configsList.length"
      v-model="currentIndex"
      :data="values"
      :removable
      editable
      size="xs"
      @click:new="handleCreate"
      @update="handleUpdate"
      @delete="handleDelete"
    />
    <UISkeleton v-else class="ui-pivot-table__configs__skeleton" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { cloneDeep } from 'lodash'

import { PivotTableConfig, PivotTableStorage } from '@types'

import { UIInputDynamicList } from '@ui/fields'
import { UISkeleton } from '@ui/skeletons'
import SidebarCaption from './SidebarCaption.vue'

type Props = {
  configsList: PivotTableConfig[]
  configIndex: number
  defaultConfig: PivotTableConfig
}

type Emits = {
  'update:storage': [data: PivotTableStorage]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const isOpened = ref(false)

const mainClasses = computed(() => ({
  'ui-pivot-table__configs--opened': isOpened.value,
}))

const values = computed(() => {
  return (
    props.configsList?.map((item, key) => ({
      value: `${key + 1}`,
      label: item.name,
    })) || []
  )
})

const removable = computed(() => values.value.length > 1)

const currentIndex = computed({
  get() {
    return `${props.configIndex + 1}`
  },
  set(value) {
    emit('update:storage', {
      configs: props.configsList,
      index: +value - 1,
    })
  },
})

const handleCreate = (name: string) => {
  const newConfig = cloneDeep(props.configsList[+currentIndex.value - 1])
  newConfig.name = name
  emit('update:storage', {
    configs: [...props.configsList, newConfig],
    index: props.configsList.length,
  })
}

const handleUpdate = (key: string, value: string) => {
  const configs = props.configsList
  configs[+key - 1].name = value
  emit('update:storage', {
    configs,
    index: props.configIndex,
  })
}

const handleDelete = (key: string) => {
  const configs = cloneDeep(props.configsList)
  const itemIndex = +key - 1
  configs.splice(itemIndex, 1)
  let index = props.configIndex
  if (itemIndex === props.configIndex) {
    index = 0
  } else if (itemIndex < props.configIndex) {
    index--
  }
  emit('update:storage', { configs, index })
}
</script>

<style>
.ui-pivot-table__configs {
  @apply hidden sm:block;
  @apply py-4 px-2 space-y-1;

  &--opened {
    @apply block;
  }

  &__skeleton {
    @apply h-[1.5rem];
  }
}
</style>
